/**
 * LinkArtifactToEventDialog
 * Dialog for linking an artifact to an event instance.
 */

import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLinkArtifactToEvent, useEcosystemEvents } from '@/hooks/useEcosystemData';

interface LinkArtifactToEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEventInstanceId?: string;
}

export function LinkArtifactToEventDialog({
  open,
  onOpenChange,
  defaultEventInstanceId,
}: LinkArtifactToEventDialogProps) {
  const [eventInstanceId, setEventInstanceId] = useState('');
  const [artifactId, setArtifactId] = useState('');
  const [sessionCode, setSessionCode] = useState('');
  const [roomOrTrack, setRoomOrTrack] = useState('');

  const linkArtifact = useLinkArtifactToEvent();
  const { data: events } = useEcosystemEvents({ onlyActive: false });

  // Fetch artifacts for dropdown
  const { data: artifacts } = useQuery({
    queryKey: ['all-artifacts-for-linking'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_artifacts')
        .select('id, title, artifact_type, provider_name')
        .eq('is_active', true)
        .order('title')
        .limit(500);
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setEventInstanceId(defaultEventInstanceId || '');
      setArtifactId('');
      setSessionCode('');
      setRoomOrTrack('');
    }
  }, [open, defaultEventInstanceId]);

  const handleSubmit = async () => {
    if (!eventInstanceId || !artifactId) {
      toast.error('Event instance and artifact are required');
      return;
    }

    try {
      await linkArtifact.mutateAsync({
        data: {
          event_instance_id: eventInstanceId,
          artifact_id: artifactId,
          session_code: sessionCode.trim() || undefined,
          room_or_track: roomOrTrack.trim() || undefined,
        },
      });
      toast.success('Artifact linked to event');
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to link artifact');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Link Artifact to Event</DialogTitle>
          <DialogDescription>Connect a training artifact to an event instance.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Event Instance *</Label>
            <Select value={eventInstanceId} onValueChange={setEventInstanceId}>
              <SelectTrigger><SelectValue placeholder="Select event" /></SelectTrigger>
              <SelectContent>
                {(events || []).map((e: any) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.series?.series_name || e.stable_key} {e.start_date ? `(${e.start_date})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Artifact *</Label>
            <Select value={artifactId} onValueChange={setArtifactId}>
              <SelectTrigger><SelectValue placeholder="Select artifact" /></SelectTrigger>
              <SelectContent>
                {(artifacts || []).map((a: any) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.title || a.id.substring(0, 8)} ({a.artifact_type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="sessionCode">Session Code</Label>
              <Input id="sessionCode" value={sessionCode} onChange={(e) => setSessionCode(e.target.value)} placeholder="e.g., A101" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="roomOrTrack">Room / Track</Label>
              <Input id="roomOrTrack" value={roomOrTrack} onChange={(e) => setRoomOrTrack(e.target.value)} placeholder="e.g., Diagnostics" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={linkArtifact.isPending}>
            {linkArtifact.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Link Artifact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
